import { useEffect, useRef } from 'react';

interface FireworksOverlayProps {
  opacity?: number;
}

export default function FireworksOverlay({ opacity = 0.8 }: FireworksOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let fireworks: Firework[] = [];
    let particles: Particle[] = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();

    // Parse CSS color string (hex or rgb/rgba) to [r,g,b]
    const parseCssColorToRgb = (input: string): [number, number, number] => {
      let c = input.trim();
      if (!c) return [10, 116, 255];
      const rgbMatch = c.match(/^rgba?\(([^)]+)\)$/i);
      if (rgbMatch) {
        const parts = rgbMatch[1].split(',').map(v => parseFloat(v.trim()));
        return [
          Math.max(0, Math.min(255, parts[0] || 0)),
          Math.max(0, Math.min(255, parts[1] || 0)),
          Math.max(0, Math.min(255, parts[2] || 0)),
        ];
      }
      if (c.startsWith('#')) c = c.slice(1);
      if (c.length === 3) c = c.split('').map(ch => ch + ch).join('');
      if (c.length !== 6) return [10, 116, 255];
      const r = parseInt(c.slice(0,2), 16);
      const g = parseInt(c.slice(2,4), 16);
      const b = parseInt(c.slice(4,6), 16);
      return [r,g,b];
    };

    // Accent rgba base string updated each frame
    let colorBase = 'rgba(10, 116, 255,';

    class Firework {
      x: number;
      y: number;
      targetY: number;
      speed: number;
      exploded: boolean;
      color: string;

      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = canvas.height;
        this.targetY = Math.random() * canvas.height * 0.5;
        this.speed = 3;
        this.exploded = false;
        // Use theme accent for firework color (updated live during draw)
        this.color = colorBase;
      }

      update() {
        if (!this.exploded) {
          this.y -= this.speed;

          if (this.y <= this.targetY) {
            this.explode();
            this.exploded = true;
          }
        }
      }

      explode() {
        const particleCount = 50;
        for (let i = 0; i < particleCount; i++) {
          particles.push(new Particle(this.x, this.y, this.color));
        }
      }

      draw() {
        if (!this.exploded) {
          ctx.beginPath();
          ctx.arc(this.x, this.y, 3, 0, Math.PI * 2);
          ctx.fillStyle = `${colorBase} 1)`;
          ctx.fill();
          
          ctx.beginPath();
          ctx.moveTo(this.x, this.y);
          ctx.lineTo(this.x, this.y + 10);
          ctx.strokeStyle = `${colorBase} 0.5)`;
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      }
    }

    class Particle {
      x: number;
      y: number;
      color: string;
      vx: number;
      vy: number;
      gravity: number;
      opacity: number;
      decay: number;
      radius: number;

      constructor(x: number, y: number, color: string) {
        this.x = x;
        this.y = y;
        this.color = color;
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 5 + 2;
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed;
        this.gravity = 0.1;
        this.opacity = 1;
        this.decay = Math.random() * 0.02 + 0.01;
        this.radius = Math.random() * 2 + 1;
      }

      update() {
        this.vx *= 0.98;
        this.vy *= 0.98;
        this.vy += this.gravity;
        this.x += this.vx;
        this.y += this.vy;
        this.opacity -= this.decay;
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        // Use current theme accent color base for live updates
        ctx.fillStyle = `${colorBase} ${this.opacity})`;
        ctx.fill();
      }
    }

    const createFirework = () => {
      if (Math.random() < 0.05) {
        fireworks.push(new Firework());
      }
    };

    const animate = () => {
      const isLightMode = document.documentElement.getAttribute('data-theme') === 'light';
      ctx.fillStyle = isLightMode ? 'rgba(248, 250, 252, 0.1)' : 'rgba(0, 0, 0, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Read header accent each frame to respond to theme/accent changes
      const accent = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-header-accent')
        .trim() || '#0A74FF';
      const [r,g,b] = parseCssColorToRgb(accent);
      colorBase = `rgba(${r}, ${g}, ${b},`;

      createFirework();

      fireworks = fireworks.filter(firework => !firework.exploded);
      fireworks.forEach(firework => {
        firework.update();
        firework.draw();
      });

      particles = particles.filter(particle => particle.opacity > 0);
      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      resizeCanvas();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [opacity]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: -1,
        opacity: opacity
      }}
    />
  );
}
